import { useState, useRef, useEffect, useMemo } from 'react';
import type { AutocompleteProps, AutocompleteOption } from '../../types';

function normalize(options: string[] | AutocompleteOption[]): AutocompleteOption[] {
  if (options.length === 0) return [];
  if (typeof options[0] === 'string') {
    return (options as string[]).map((o) => ({ label: o, keywords: [] }));
  }
  return options as AutocompleteOption[];
}

export default function Autocomplete({
  label,
  placeholder,
  value,
  onChange,
  options,
  error,
  helper,
  disabled = false,
  className = '',
}: AutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const normalized = useMemo(() => normalize(options), [options]);

  const filteredOptions = useMemo(() => {
    const search = value.toLowerCase();
    return normalized.filter(
      (opt) =>
        opt.label.toLowerCase().includes(search) ||
        opt.keywords.some((kw) => kw.toLowerCase().includes(search))
    );
  }, [normalized, value]);

  const containerClasses = [
    'autocomplete-container',
    error && 'autocomplete-container--error',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleInputChange(newValue: string) {
    onChange(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  }

  function handleOptionClick(option: AutocompleteOption) {
    onChange(option.label);
    setIsOpen(false);
    inputRef.current?.blur();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  }

  return (
    <div className={containerClasses} ref={containerRef}>
      {label && <label className="autocomplete__label">{label}</label>}
      <div className="autocomplete__wrapper">
        <input
          ref={inputRef}
          type="text"
          className="autocomplete__input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
        />
        <span className="autocomplete__icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        {isOpen && filteredOptions.length > 0 && (
          <ul className="autocomplete__dropdown" role="listbox">
            {filteredOptions.map((option, index) => (
              <li
                key={option.label}
                className={[
                  'autocomplete__option',
                  index === highlightedIndex && 'autocomplete__option--highlighted',
                  option.label === value && 'autocomplete__option--selected',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleOptionClick(option)}
                role="option"
                aria-selected={option.label === value}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
        {isOpen && value && filteredOptions.length === 0 && (
          <div className="autocomplete__dropdown">
            <div className="autocomplete__no-results">No results found</div>
          </div>
        )}
      </div>
      {error && <span className="autocomplete__error">{error}</span>}
      {helper && !error && <span className="autocomplete__helper">{helper}</span>}
    </div>
  );
}
